import { isNullish } from "@tinyurldev/tu-ui-vue";

import {
  isSubscriptionLegacy,
  isSubscriptionLegacyPro,
  isSubscriptionLegacyBulk,
} from "@Helpers/functions/isSubscriptionLegacy";

import SUBSCRIPTION_INTERVAL from "@Constants/subscription-interval";

export default class BillableSubscription {
  constructor(params) {
    this.name = null;
    this.interval = null;
    this.amount = null;
    this.currency = null;
    this.quantity = null;
    this.ends_at = null;
    this.trial_ends_at = null;
    this.billing_status = null;
    this.created_at = null;
    this.updated_at = null;
    this.period_ends_at = null;

    Object.assign(this, params);
  }

  get isActive() {
    return isNullish(this.ends_at);
  }

  get isOnTrial() {
    return (
      this.trial_ends_at &&
      moment.utc().isBefore(moment.utc(this.trial_ends_at))
    );
  }

  get isOnGracePeriod() {
    return !this.isActive && moment.utc().isBefore(moment.utc(this.ends_at));
  }

  get isPastDue() {
    return this.billing_status === "past_due";
  }

  get isIncomplete() {
    return this.billing_status === "incomplete";
  }

  get isFree() {
    return this.name === "free";
  }

  get isLegacy() {
    if (isNullish(this)) {
      return false;
    }

    return isSubscriptionLegacy(this);
  }

  get isLegacyPro() {
    if (isNullish(this)) {
      return false;
    }

    return isSubscriptionLegacyPro(this);
  }

  get isLegacyBulk() {
    if (isNullish(this)) {
      return false;
    }

    return isSubscriptionLegacyBulk(this);
  }

  get needAction() {
    return this.isActive && (this.isPastDue || this.isIncomplete);
  }

  get trialEndsAt() {
    if (!this.isOnTrial) return;
    return moment.utc(this.trial_ends_at).local().format("MMMM Do, YYYY");
  }

  get nextRenewalAt() {
    if (!this.isActive) {
      return;
    }

    return this.prepareNextRenewalAt().format("MMMM Do, YYYY");
  }

  get nextRenewalDateTimeAt() {
    if (!this.isActive) {
      return;
    }

    return this.prepareNextRenewalAt().format("MMMM Do, YYYY hh:mmA");
  }

  get periodEndsAt() {
    if (!this.isActive) {
      return;
    }

    return moment.utc(this.period_ends_at).local().format("MMMM Do, YYYY");
  }

  get periodEndsDateTimeAt() {
    if (!this.isActive) {
      return;
    }

    return moment
      .utc(this.period_ends_at)
      .local()
      .format("MMMM Do, YYYY hh:mmA");
  }

  get endDate() {
    if (!this.isActive) {
      return moment.utc(this.ends_at).local().format("MMMM Do, YYYY");
    }
  }

  get remainingTrialDays() {
    if (!this.isOnTrial) return;

    const current = moment.utc().valueOf();
    const trial = moment.utc(this.trial_ends_at).valueOf();
    const difference = trial - current;
    const remaining = moment.duration(difference).asDays();
    return remaining;
  }

  /**
   * Prepares the "next renewal at" date object before formatting.
   *
   * @returns The date object ready for formatting.
   *
   * @remarks It adds, depending on the interval, a duration of 1 year or 1 month.
   * This is schedule to be changed for new properties period_ends_at and period_starts_at.
   */
  prepareNextRenewalAt() {
    const duration = this.interval === SUBSCRIPTION_INTERVAL.YEARLY
      ? moment.duration("P1Y")
      : moment.duration("P1M");

    return moment.utc(this.updated_at).add(duration).local();
  }
}
